import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import apiClient from '@/libs/apiClient'
import type { EmailCampaign } from '@/types/marketing'

interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface EmailCampaignState {
  campaigns: EmailCampaign[]
  pagination: Pagination | null
  loading: boolean
  error: string | null
}

const initialState: EmailCampaignState = {
  campaigns: [],
  pagination: null,
  loading: false,
  error: null
}

// Fetch all email campaigns (promotions)
export const fetchEmailCampaigns = createAsyncThunk<
  { campaigns: EmailCampaign[]; pagination: Pagination },
  { limit?: number } | undefined,
  { rejectValue: string }
>('emailCampaigns/fetchEmailCampaigns', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const res = await apiClient.get('/admin/v1/email-campaigns', { params: { limit } })
    const json = res.data

    const pagination: Pagination = {
      currentPage: json.meta?.currentPage || 1,
      lastPage: json.meta?.lastPage || 1,
      perPage: json.meta?.perPage || 25,
      total: json.meta?.total || 0
    }

    return { campaigns: json.data as EmailCampaign[], pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch email campaigns')
  }
})

// Add Campaign
export const addEmailCampaign = createAsyncThunk<EmailCampaign, FormData, { rejectValue: string }>(
  'emailCampaigns/addEmailCampaign',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/email-campaigns/add', formData)
      const data = res.data

      return data.data as EmailCampaign
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add email campaign')
    }
  }
)

// Update Campaign
export const updateEmailCampaign = createAsyncThunk<EmailCampaign, { id: string; formData: FormData }, { rejectValue: string }>(
  'emailCampaigns/updateEmailCampaign',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/email-campaigns/${id}/edit`, formData)
      const data = res.data

      return data.data as EmailCampaign
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update email campaign')
    }
  }
)

// Delete Campaign
export const deleteEmailCampaign = createAsyncThunk<string, string, { rejectValue: string }>(
  'emailCampaigns/deleteEmailCampaign',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/email-campaigns/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete email campaign')
    }
  }
)

// Send Campaign Now
export const sendEmailCampaign = createAsyncThunk<EmailCampaign, string, { rejectValue: string }>(
  'emailCampaigns/sendEmailCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/email-campaigns/${id}/send`)
      const data = res.data

      return data.data as EmailCampaign
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to send email campaign')
    }
  }
)

const emailCampaignSlice = createSlice({
  name: 'emailCampaigns',
  initialState,
  reducers: {
    clearEmailCampaigns(state) {
      state.campaigns = []
      state.pagination = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder

      // Fetch
      .addCase(fetchEmailCampaigns.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEmailCampaigns.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = action.payload.campaigns
        state.pagination = action.payload.pagination
      })
      .addCase(fetchEmailCampaigns.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch email campaigns'
      })

      // Add
      .addCase(addEmailCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addEmailCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns.unshift(action.payload)
        if (state.pagination) state.pagination.total += 1
      })
      .addCase(addEmailCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to add email campaign'
      })

      // Update
      .addCase(updateEmailCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateEmailCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = state.campaigns.map(c => (c.id === action.payload.id ? action.payload : c))
      })
      .addCase(updateEmailCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update email campaign'
      })

      // Delete
      .addCase(deleteEmailCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteEmailCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = state.campaigns.filter(c => c.id !== action.payload)
        if (state.pagination) state.pagination.total -= 1
      })
      .addCase(deleteEmailCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete email campaign'
      })

      // Send
      .addCase(sendEmailCampaign.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(sendEmailCampaign.fulfilled, (state, action) => {
        state.loading = false
        state.campaigns = state.campaigns.map(c => (c.id === action.payload.id ? action.payload : c))
      })
      .addCase(sendEmailCampaign.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to send email campaign'
      })
  }
})

export const { clearEmailCampaigns } = emailCampaignSlice.actions
export default emailCampaignSlice.reducer
